import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Button,
  Card,
  Form,
  Grid,
  Label,
  Message,
  Statistic,
  Table,
} from 'semantic-ui-react';
import { useParams, useNavigate } from 'react-router-dom';
import { API, showError, showSuccess } from '../../helpers';
import { renderQuota, renderQuotaWithPrompt } from '../../helpers/render';

const centsToCurrency = (amount) => `CNY ${(Number(amount || 0) / 100).toFixed(2)}`;

const formatTime = (timestamp) => {
  if (!timestamp) {
    return '-';
  }
  return new Date(timestamp * 1000).toLocaleString();
};

const renderPaymentStatus = (status) => {
  switch (status) {
    case 'paid':
      return <Label color='green'>Paid</Label>;
    case 'closed':
      return <Label color='grey'>Closed</Label>;
    case 'error':
      return <Label color='red'>Error</Label>;
    default:
      return <Label color='blue'>Pending</Label>;
  }
};

const renderWalletDirection = (direction) => {
  return direction === 'expense' ? (
    <Label color='orange' basic>
      Expense
    </Label>
  ) : (
    <Label color='green' basic>
      Income
    </Label>
  );
};

const EditUser = () => {
  const { t } = useTranslation();
  const params = useParams();
  const userId = params.id;
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [inputs, setInputs] = useState({
    username: '',
    display_name: '',
    password: '',
    github_id: '',
    wechat_id: '',
    email: '',
    quota: 0,
    group: 'default',
    total_recharge_amount: 0,
    total_consume_amount: 0,
  });
  const [groupOptions, setGroupOptions] = useState([]);
  const [wallet, setWallet] = useState(null);
  const [walletTransactions, setWalletTransactions] = useState([]);
  const [paymentOrders, setPaymentOrders] = useState([]);
  const [billingSummary, setBillingSummary] = useState(null);
  const [billingItems, setBillingItems] = useState([]);
  const [audits, setAudits] = useState([]);

  const {
    username,
    display_name,
    password,
    github_id,
    wechat_id,
    email,
    quota,
  } = inputs;

  const handleInputChange = (e, { name, value }) => {
    setInputs((current) => ({ ...current, [name]: value }));
  };

  const fetchGroups = useCallback(async () => {
    try {
      const res = await API.get('/api/group/');
      setGroupOptions(
        (res.data.data || []).map((group) => ({
          key: group,
          text: group,
          value: group,
        }))
      );
    } catch (error) {
      showError(error.message);
    }
  }, []);

  const loadUser = useCallback(async () => {
    const res = userId
      ? await API.get(`/api/user/${userId}`)
      : await API.get('/api/user/self');
    const { success, message, data } = res.data;
    if (!success) {
      showError(message);
      return;
    }
    setInputs({
      ...data,
      password: '',
    });
  }, [userId]);

  const loadUserDetails = useCallback(async () => {
    if (!userId) {
      return;
    }
    setDetailsLoading(true);
    try {
      const [
        walletRes,
        walletTxRes,
        paymentRes,
        billingSummaryRes,
        billingItemsRes,
        auditsRes,
      ] = await Promise.all([
        API.get(`/api/admin/users/${userId}/wallet`),
        API.get(`/api/admin/users/${userId}/wallet/transactions`),
        API.get(`/api/admin/payment/orders?user_id=${userId}`),
        API.get(`/api/admin/billing/summary?user_id=${userId}`),
        API.get(`/api/admin/billing/items?user_id=${userId}`),
        API.get(`/api/admin/audits?target_type=user&target_id=${userId}`),
      ]);

      const responses = [
        walletRes.data,
        walletTxRes.data,
        paymentRes.data,
        billingSummaryRes.data,
        billingItemsRes.data,
        auditsRes.data,
      ];
      const failed = responses.find((item) => !item.success);
      if (failed) {
        showError(failed.message || 'Failed to load user details');
        return;
      }

      setWallet(walletRes.data.data || null);
      setWalletTransactions(walletTxRes.data.data || []);
      setPaymentOrders(paymentRes.data.data || []);
      setBillingSummary(billingSummaryRes.data.data || null);
      setBillingItems(billingItemsRes.data.data || []);
      setAudits(auditsRes.data.data || []);
    } catch (error) {
      showError(error.message || 'Failed to load user details');
    } finally {
      setDetailsLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    const init = async () => {
      setLoading(true);
      await loadUser();
      if (userId) {
        await fetchGroups();
        await loadUserDetails();
      }
      setLoading(false);
    };
    init().then();
  }, [fetchGroups, loadUser, loadUserDetails, userId]);

  const handleCancel = () => {
    navigate(userId ? '/user' : '/setting');
  };

  const submit = async () => {
    const res = userId
      ? await API.put('/api/user/', {
          ...inputs,
          id: parseInt(userId, 10),
          quota: typeof inputs.quota === 'string' ? parseInt(inputs.quota, 10) : inputs.quota,
        })
      : await API.put('/api/user/self', inputs);
    const { success, message } = res.data;
    if (!success) {
      showError(message);
      return;
    }
    showSuccess(t('user.messages.update_success'));
    await loadUser();
  };

  return (
    <div className='dashboard-container'>
      <Card fluid className='chart-card'>
        <Card.Content>
          <Card.Header className='header'>{t('user.edit.title')}</Card.Header>
          <Form loading={loading} autoComplete='new-password'>
            <Form.Field>
              <Form.Input
                label={t('user.edit.username')}
                name='username'
                placeholder={t('user.edit.username_placeholder')}
                onChange={handleInputChange}
                value={username}
                autoComplete='new-password'
              />
            </Form.Field>
            <Form.Field>
              <Form.Input
                label={t('user.edit.password')}
                name='password'
                type='password'
                placeholder={t('user.edit.password_placeholder')}
                onChange={handleInputChange}
                value={password}
                autoComplete='new-password'
              />
            </Form.Field>
            <Form.Field>
              <Form.Input
                label={t('user.edit.display_name')}
                name='display_name'
                placeholder={t('user.edit.display_name_placeholder')}
                onChange={handleInputChange}
                value={display_name}
                autoComplete='new-password'
              />
            </Form.Field>
            {userId ? (
              <>
                <Form.Field>
                  <Form.Dropdown
                    label={t('user.edit.group')}
                    placeholder={t('user.edit.group_placeholder')}
                    name='group'
                    fluid
                    search
                    selection
                    allowAdditions
                    additionLabel={t('user.edit.group_addition')}
                    onChange={handleInputChange}
                    value={inputs.group}
                    autoComplete='new-password'
                    options={groupOptions}
                  />
                </Form.Field>
                <Form.Field>
                  <Form.Input
                    label={`${t('user.edit.quota')}${renderQuotaWithPrompt(quota, t)}`}
                    name='quota'
                    placeholder={t('user.edit.quota_placeholder')}
                    onChange={handleInputChange}
                    value={quota}
                    type='number'
                    autoComplete='new-password'
                  />
                </Form.Field>
              </>
            ) : null}
            <Form.Field>
              <Form.Input
                label={t('user.edit.github_id')}
                name='github_id'
                value={github_id}
                autoComplete='new-password'
                placeholder={t('user.edit.github_id_placeholder')}
                readOnly
              />
            </Form.Field>
            <Form.Field>
              <Form.Input
                label={t('user.edit.wechat_id')}
                name='wechat_id'
                value={wechat_id}
                autoComplete='new-password'
                placeholder={t('user.edit.wechat_id_placeholder')}
                readOnly
              />
            </Form.Field>
            <Form.Field>
              <Form.Input
                label={t('user.edit.email')}
                name='email'
                value={email}
                autoComplete='new-password'
                placeholder={t('user.edit.email_placeholder')}
                onChange={handleInputChange}
              />
            </Form.Field>
            <Form.Group widths='equal'>
              <Form.Input
                label='Mobile'
                name='mobile'
                value={inputs.mobile || ''}
                autoComplete='new-password'
                placeholder='Mobile'
                onChange={handleInputChange}
              />
              <Form.Input
                label='Company'
                name='company'
                value={inputs.company || ''}
                autoComplete='new-password'
                placeholder='Company'
                onChange={handleInputChange}
              />
            </Form.Group>
            <Form.Group widths='equal'>
              <Form.Input
                label='Department'
                name='department'
                value={inputs.department || ''}
                autoComplete='new-password'
                placeholder='Department'
                onChange={handleInputChange}
              />
              <Form.Input
                label='Source'
                name='source'
                value={inputs.source || ''}
                autoComplete='new-password'
                placeholder='Source'
                onChange={handleInputChange}
              />
            </Form.Group>
            <Form.Group widths='equal'>
              <Form.Input
                label='Sales Owner'
                name='sales_owner'
                value={inputs.sales_owner || ''}
                autoComplete='new-password'
                placeholder='Sales Owner'
                onChange={handleInputChange}
              />
              <Form.Input
                label='Customer Level'
                name='customer_level'
                value={inputs.customer_level || ''}
                autoComplete='new-password'
                placeholder='Customer Level'
                onChange={handleInputChange}
              />
            </Form.Group>
            <Form.Group widths='equal'>
              <Form.Input
                label='Status Reason'
                name='status_reason'
                value={inputs.status_reason || ''}
                autoComplete='new-password'
                placeholder='Status Reason'
                onChange={handleInputChange}
              />
              <Form.Checkbox
                style={{ marginTop: '2rem' }}
                label='Wallet Enabled'
                checked={inputs.wallet_enabled !== false}
                onChange={(e, { checked }) =>
                  setInputs((current) => ({ ...current, wallet_enabled: checked }))
                }
              />
            </Form.Group>
            <Form.Field>
              <Form.TextArea
                label='Remark'
                name='remark'
                value={inputs.remark || ''}
                placeholder='Remark'
                onChange={handleInputChange}
              />
            </Form.Field>
            <Button onClick={handleCancel}>{t('user.edit.buttons.cancel')}</Button>
            <Button positive onClick={submit}>
              {t('user.edit.buttons.submit')}
            </Button>
          </Form>
        </Card.Content>
      </Card>

      {userId ? (
        <>
          <Card fluid className='chart-card'>
            <Card.Content>
              <Card.Header className='header'>
                User Overview
                <Button
                  floated='right'
                  size='small'
                  loading={detailsLoading}
                  onClick={() => loadUserDetails()}
                >
                  Refresh
                </Button>
              </Card.Header>
              <Grid columns={4} stackable>
                <Grid.Column>
                  <Statistic size='small'>
                    <Statistic.Value>{renderQuota(inputs.quota || 0, t)}</Statistic.Value>
                    <Statistic.Label>Quota</Statistic.Label>
                  </Statistic>
                </Grid.Column>
                <Grid.Column>
                  <Statistic size='small'>
                    <Statistic.Value>
                      {wallet ? centsToCurrency(wallet.available_amount) : '-'}
                    </Statistic.Value>
                    <Statistic.Label>Wallet Balance</Statistic.Label>
                  </Statistic>
                </Grid.Column>
                <Grid.Column>
                  <Statistic size='small'>
                    <Statistic.Value>{centsToCurrency(inputs.total_recharge_amount)}</Statistic.Value>
                    <Statistic.Label>Total Recharge</Statistic.Label>
                  </Statistic>
                </Grid.Column>
                <Grid.Column>
                  <Statistic size='small'>
                    <Statistic.Value>{centsToCurrency(inputs.total_consume_amount)}</Statistic.Value>
                    <Statistic.Label>Total Consume</Statistic.Label>
                  </Statistic>
                </Grid.Column>
              </Grid>
            </Card.Content>
          </Card>

          <Grid stackable columns={2}>
            <Grid.Column width={8}>
              <Card fluid className='chart-card'>
                <Card.Content>
                  <Card.Header className='header'>Wallet Transactions</Card.Header>
                  {walletTransactions.length === 0 ? (
                    <Message info content='No wallet transactions found.' />
                  ) : (
                    <Table basic='very' compact size='small'>
                      <Table.Header>
                        <Table.Row>
                          <Table.HeaderCell>Time</Table.HeaderCell>
                          <Table.HeaderCell>Type</Table.HeaderCell>
                          <Table.HeaderCell>Amount</Table.HeaderCell>
                          <Table.HeaderCell>After</Table.HeaderCell>
                          <Table.HeaderCell>Remark</Table.HeaderCell>
                        </Table.Row>
                      </Table.Header>
                      <Table.Body>
                        {walletTransactions.map((record) => (
                          <Table.Row key={record.id}>
                            <Table.Cell>{formatTime(record.created_at)}</Table.Cell>
                            <Table.Cell>{renderWalletDirection(record.direction)}</Table.Cell>
                            <Table.Cell>
                              {record.direction === 'expense' ? '-' : '+'}
                              {centsToCurrency(record.amount)}
                            </Table.Cell>
                            <Table.Cell>{centsToCurrency(record.after_amount)}</Table.Cell>
                            <Table.Cell>{record.remark || '-'}</Table.Cell>
                          </Table.Row>
                        ))}
                      </Table.Body>
                    </Table>
                  )}
                </Card.Content>
              </Card>

              <Card fluid className='chart-card'>
                <Card.Content>
                  <Card.Header className='header'>Payment Orders</Card.Header>
                  {paymentOrders.length === 0 ? (
                    <Message info content='No payment orders found.' />
                  ) : (
                    <Table basic='very' compact size='small'>
                      <Table.Header>
                        <Table.Row>
                          <Table.HeaderCell>Order No.</Table.HeaderCell>
                          <Table.HeaderCell>Product</Table.HeaderCell>
                          <Table.HeaderCell>Amount</Table.HeaderCell>
                          <Table.HeaderCell>Status</Table.HeaderCell>
                          <Table.HeaderCell>Created At</Table.HeaderCell>
                        </Table.Row>
                      </Table.Header>
                      <Table.Body>
                        {paymentOrders.map((order) => (
                          <Table.Row key={order.order_no}>
                            <Table.Cell>{order.order_no}</Table.Cell>
                            <Table.Cell>{order.product_name || '-'}</Table.Cell>
                            <Table.Cell>{centsToCurrency(order.pay_amount || order.amount)}</Table.Cell>
                            <Table.Cell>{renderPaymentStatus(order.status)}</Table.Cell>
                            <Table.Cell>{formatTime(order.created_at)}</Table.Cell>
                          </Table.Row>
                        ))}
                      </Table.Body>
                    </Table>
                  )}
                </Card.Content>
              </Card>
            </Grid.Column>

            <Grid.Column width={8}>
              <Card fluid className='chart-card'>
                <Card.Content>
                  <Card.Header className='header'>Billing Summary</Card.Header>
                  <Grid columns={2} stackable>
                    <Grid.Column>
                      <Statistic size='small'>
                        <Statistic.Value>{billingSummary?.item_count || 0}</Statistic.Value>
                        <Statistic.Label>Items</Statistic.Label>
                      </Statistic>
                    </Grid.Column>
                    <Grid.Column>
                      <Statistic size='small'>
                        <Statistic.Value>{billingSummary?.total_tokens || 0}</Statistic.Value>
                        <Statistic.Label>Tokens</Statistic.Label>
                      </Statistic>
                    </Grid.Column>
                    <Grid.Column>
                      <Statistic size='small'>
                        <Statistic.Value>
                          {renderQuota(billingSummary?.quota_amount || 0, t, 4)}
                        </Statistic.Value>
                        <Statistic.Label>Quota</Statistic.Label>
                      </Statistic>
                    </Grid.Column>
                    <Grid.Column>
                      <Statistic size='small'>
                        <Statistic.Value>
                          {centsToCurrency(billingSummary?.settlement_amount || 0)}
                        </Statistic.Value>
                        <Statistic.Label>Settlement</Statistic.Label>
                      </Statistic>
                    </Grid.Column>
                  </Grid>
                </Card.Content>
              </Card>

              <Card fluid className='chart-card'>
                <Card.Content>
                  <Card.Header className='header'>Recent Billing</Card.Header>
                  {billingItems.length === 0 ? (
                    <Message info content='No billing items found.' />
                  ) : (
                    <Table basic='very' compact size='small'>
                      <Table.Header>
                        <Table.Row>
                          <Table.HeaderCell>Time</Table.HeaderCell>
                          <Table.HeaderCell>Model</Table.HeaderCell>
                          <Table.HeaderCell>Type</Table.HeaderCell>
                          <Table.HeaderCell>Tokens</Table.HeaderCell>
                          <Table.HeaderCell>Settlement</Table.HeaderCell>
                        </Table.Row>
                      </Table.Header>
                      <Table.Body>
                        {billingItems.map((item) => (
                          <Table.Row key={item.id}>
                            <Table.Cell>{formatTime(item.created_at)}</Table.Cell>
                            <Table.Cell>{item.model_name}</Table.Cell>
                            <Table.Cell>{item.biz_type}</Table.Cell>
                            <Table.Cell>{item.total_tokens}</Table.Cell>
                            <Table.Cell>{centsToCurrency(item.settlement_amount)}</Table.Cell>
                          </Table.Row>
                        ))}
                      </Table.Body>
                    </Table>
                  )}
                </Card.Content>
              </Card>

              <Card fluid className='chart-card'>
                <Card.Content>
                  <Card.Header className='header'>Operation Audits</Card.Header>
                  {audits.length === 0 ? (
                    <Message info content='No audits found for this user.' />
                  ) : (
                    <Table basic='very' compact size='small'>
                      <Table.Header>
                        <Table.Row>
                          <Table.HeaderCell>Time</Table.HeaderCell>
                          <Table.HeaderCell>Operator</Table.HeaderCell>
                          <Table.HeaderCell>Action</Table.HeaderCell>
                          <Table.HeaderCell>Remark</Table.HeaderCell>
                        </Table.Row>
                      </Table.Header>
                      <Table.Body>
                        {audits.map((audit) => (
                          <Table.Row key={audit.id}>
                            <Table.Cell>{formatTime(audit.created_at)}</Table.Cell>
                            <Table.Cell>{audit.operator_name || audit.operator_id}</Table.Cell>
                            <Table.Cell>{audit.action}</Table.Cell>
                            <Table.Cell>{audit.remark || '-'}</Table.Cell>
                          </Table.Row>
                        ))}
                      </Table.Body>
                    </Table>
                  )}
                </Card.Content>
              </Card>
            </Grid.Column>
          </Grid>
        </>
      ) : null}
    </div>
  );
};

export default EditUser;
